import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, NavLink } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Providers
import { ShopifyAppBridgeProvider } from "./providers/ShopifyAppBridgeProvider";

// Components
import PriceChangeNotification from "./components/PriceChangeNotification";
import OnboardingWizard from "./components/OnboardingWizard";
import ShopifyConnectionBanner from "./components/ShopifyConnectionBanner";

// Pages
import PricingPage from "./pages/PricingPage";
import DashboardPage from "./pages/DashboardPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import WaybillsPage from "./pages/WaybillsPage";
import SettingsPage from "./pages/SettingsPage";
import CreateWaybillPage from "./pages/CreateWaybillPage";

// Wrapper that forces remount when navigating between different modes or orders
const CreateWaybillPageWrapper = ({ mode }) => {
  const routerLocation = useLocation();
  return <CreateWaybillPage key={`${mode}-${routerLocation.key}`} />;
};
import PackagesPage from "./pages/PackagesPage";
import ManifestPage from "./pages/ManifestPage";
import ShopifyOrderWaybillPage from "./pages/ShopifyOrderWaybillPage";
import LoginPage from "./pages/LoginPage";
import QuotesPage from "./pages/QuotesPage";
// CancelWaybillPage removed - EasyWaybill API does not support cancellation
import AddressesPage from "./pages/AddressesPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AuthPage from "./pages/AuthPage";
import SuperAdminPage from "./pages/SuperAdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("ff_authenticated") === "true";
  });
  const [user, setUser] = useState(() => {
    return localStorage.getItem("ff_user") || null;
  });

  const login = (success) => {
    setIsAuthenticated(success);
    if (success) {
      setUser(localStorage.getItem("ff_user"));
    }
  };

  const logout = () => {
    localStorage.removeItem("ff_authenticated");
    localStorage.removeItem("ff_user");
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Context (for shared state)
const AppContext = createContext(null);

export const useAppContext = () => useContext(AppContext);

const AppProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [packages, setPackages] = useState([]);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchSettings();
    fetchPackages();
  }, [isAuthenticated]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching packages:", error);
      return [];
    }
  };

  return (
    <AppContext.Provider value={{ settings, setSettings, packages, setPackages, fetchSettings, fetchPackages }}>
      {children}
    </AppContext.Provider>
  );
};

// Sidebar Navigation Component with EasyWaybill Menu Structure
const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [expandedMenu, setExpandedMenu] = useState(null);
  
  // Menu structure - dropdowns
  const menuItems = [
    { 
      id: "quotes",
      label: "Quotes", 
      icon: "📋",
      subItems: [
        { path: "/quotes/request", label: "Get Quote" },
        { path: "/quotes", label: "View Saved Quotes" },
      ]
    },
    { 
      id: "collections",
      label: "Returns", 
      icon: "📦",
      subItems: [
        { path: "/collections/elsewhere", label: "Book Return" },
        { path: "/collections", label: "View Returns" },
      ]
    },
  ];

  // Waybills - direct link (no sub-menu)
  const waybillsNavItem = { 
    path: "/waybills",
    label: "Waybills", 
    icon: "📄"
  };

  const manifestNavItem = {
    path: "/manifest",
    label: "Manifest",
    icon: "📋"
  };

  // Direct nav items (no sub-menu) - ordered by hierarchy
  const directNavItems = [
    { path: "/orders", label: "Book Orders", icon: "🛒" },
  ];

  // My Account menu (shown after direct nav items)
  const accountMenu = { 
    id: "account",
    label: "My Account", 
    icon: "⚙️",
    subItems: [
      { path: "/addresses", label: "Manage Addresses" },
      { path: "/packages", label: "Package Presets" },
      { path: "/pricing", label: "Pricing" },
      { path: "/settings", label: "Settings" },
    ]
  };

  const isActive = (path) => {
    if (path === "/quotes" || path === "/collections" || path === "/orders") {
      return location.pathname === path;
    }
    if (path === "/quotes/request") {
      return location.pathname === "/quotes/request";
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isMenuActive = (menu) => {
    return menu.subItems.some(item => isActive(item.path));
  };

  const toggleMenu = (menuId) => {
    setExpandedMenu(expandedMenu === menuId ? null : menuId);
  };

  return (
    <div className="w-56 bg-[#1A1A1A] h-screen flex flex-col fixed left-0 top-0">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <img src="/easywaybill-icon.webp" alt="EasyWaybill" className="w-8 h-8 rounded" />
          <div>
            <h1 className="text-white font-semibold text-sm">EasyWaybill</h1>
            <p className="text-gray-500 text-xs">Waybill Management</p>
          </div>
        </div>
      </div>

      {/* Navigation - scrollable */}
      <nav className="flex-1 p-2 overflow-y-auto">
        {/* 1. Book Orders */}
        <NavLink
          to="/orders"
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors mb-1 ${
            isActive("/orders")
              ? "bg-[#2A2A2A] text-white"
              : "text-gray-400 hover:bg-[#2A2A2A] hover:text-white"
          }`}
        >
          <span>🛒</span>
          <span className="text-sm">Book Orders</span>
        </NavLink>

        {/* 2. Quotes dropdown */}
        {menuItems.filter(m => m.id === "quotes").map((menu) => (
          <div key={menu.id} className="mb-1">
            <button
              onClick={() => toggleMenu(menu.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors ${
                isMenuActive(menu) || expandedMenu === menu.id
                  ? "bg-[#2A2A2A] text-white"
                  : "text-gray-400 hover:bg-[#2A2A2A] hover:text-white"
              }`}
            >
              <div className="flex items-center gap-3">
                <span>{menu.icon}</span>
                <span className="text-sm">{menu.label}</span>
              </div>
              <span className={`text-xs transition-transform ${expandedMenu === menu.id ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {(expandedMenu === menu.id || isMenuActive(menu)) && (
              <div className="ml-4 mt-1 space-y-1">
                {menu.subItems.map((item) => (
                  <NavLink key={item.path} to={item.path}
                    className={`block px-3 py-1.5 rounded text-xs transition-colors ${
                      isActive(item.path) ? "bg-green-600/20 text-green-500" : "text-gray-500 hover:text-gray-300 hover:bg-[#2A2A2A]"
                    }`}>{item.label}</NavLink>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* 3. Collections dropdown */}
        {menuItems.filter(m => m.id === "collections").map((menu) => (
          <div key={menu.id} className="mb-1">
            <button
              onClick={() => toggleMenu(menu.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors ${
                isMenuActive(menu) || expandedMenu === menu.id
                  ? "bg-[#2A2A2A] text-white"
                  : "text-gray-400 hover:bg-[#2A2A2A] hover:text-white"
              }`}
            >
              <div className="flex items-center gap-3">
                <span>{menu.icon}</span>
                <span className="text-sm">{menu.label}</span>
              </div>
              <span className={`text-xs transition-transform ${expandedMenu === menu.id ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {(expandedMenu === menu.id || isMenuActive(menu)) && (
              <div className="ml-4 mt-1 space-y-1">
                {menu.subItems.map((item) => (
                  <NavLink key={item.path} to={item.path}
                    className={`block px-3 py-1.5 rounded text-xs transition-colors ${
                      isActive(item.path) ? "bg-green-600/20 text-green-500" : "text-gray-500 hover:text-gray-300 hover:bg-[#2A2A2A]"
                    }`}>{item.label}</NavLink>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* 4. Waybills - Direct link (no dropdown) */}
        <NavLink
          to={waybillsNavItem.path}
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors mb-1 ${
            isActive(waybillsNavItem.path)
              ? "bg-[#2A2A2A] text-white"
              : "text-gray-400 hover:bg-[#2A2A2A] hover:text-white"
          }`}
        >
          <span>{waybillsNavItem.icon}</span>
          <span className="text-sm">{waybillsNavItem.label}</span>
        </NavLink>

        {/* 5. Manifest - Direct link */}
        <NavLink
          to={manifestNavItem.path}
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors mb-1 ${
            isActive(manifestNavItem.path)
              ? "bg-[#2A2A2A] text-white"
              : "text-gray-400 hover:bg-[#2A2A2A] hover:text-white"
          }`}
        >
          <span>{manifestNavItem.icon}</span>
          <span className="text-sm">{manifestNavItem.label}</span>
        </NavLink>

        {/* My Account menu (after PODs) */}
        <div className="mb-1">
          <button
            onClick={() => toggleMenu(accountMenu.id)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors ${
              accountMenu.subItems.some(item => isActive(item.path)) || expandedMenu === accountMenu.id
                ? "bg-[#2A2A2A] text-white"
                : "text-gray-400 hover:bg-[#2A2A2A] hover:text-white"
            }`}
          >
            <div className="flex items-center gap-3">
              <span>{accountMenu.icon}</span>
              <span className="text-sm">{accountMenu.label}</span>
            </div>
            <span className={`text-xs transition-transform ${expandedMenu === accountMenu.id ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
          
          {(expandedMenu === accountMenu.id || accountMenu.subItems.some(item => isActive(item.path))) && (
            <div className="ml-4 mt-1 space-y-1">
              {accountMenu.subItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-1.5 rounded text-xs transition-colors ${
                    isActive(item.path)
                      ? "bg-green-600/20 text-green-500"
                      : "text-gray-500 hover:text-gray-300 hover:bg-[#2A2A2A]"
                  }`}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Footer - always visible */}
      <div className="p-4 border-t border-gray-800 flex-shrink-0">
        {user && (
          <div className="mb-2">
            <p className="text-xs text-gray-500 truncate">{user}</p>
          </div>
        )}
        <button
          onClick={logout}
          className="w-full px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-[#2A2A2A] rounded transition-colors flex items-center justify-center gap-2"
          data-testid="logout-btn"
        >
          <span>🚪</span>
          Sign Out
        </button>
      </div>
    </div>
  );
};

// Layout with fixed sidebar
const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen">
      <Sidebar />
      <div className="ml-56 bg-[#F6F6F7] min-h-screen overflow-auto">
        <ShopifyConnectionBanner />
        {children}
      </div>
      <PriceChangeNotification />
    </div>
  );
};

// Shopify Session Token Axios Interceptor
// When embedded in Shopify admin, attach session tokens to every API request
const setupSessionTokenInterceptor = () => {
  axios.interceptors.request.use(async (config) => {
    // Only intercept API calls to our backend
    if (!config.url?.includes('/api/')) return config;
    
    // Never touch super-admin requests — they use their own session tokens
    if (config.url?.includes('/api/super-admin/')) return config;
    
    // If we're in Shopify embedded mode, use session tokens
    if (window.shopify && window.__SHOPIFY_EMBEDDED__) {
      try {
        const token = await window.shopify.idToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
          config.headers['X-Shopify-Session-Token'] = token;
        }
      } catch (err) {
        console.warn('Failed to get session token:', err);
      }
    } else {
      // Non-embedded: attach JWT from localStorage if not already set
      const existing = config.headers?.['Authorization'] || config.headers?.get?.('Authorization');
      if (!existing) {
        const jwt = localStorage.getItem("ff_access_token");
        if (jwt) {
          config.headers['Authorization'] = `Bearer ${jwt}`;
        }
      }
    }
    return config;
  });
};

// Install immediately at module scope so the very first request already carries the JWT
setupSessionTokenInterceptor();

// Billing gate — blocks the app when there's no active Shopify subscription / trial
const BillingGate = ({ children }) => {
  const [state, setState] = useState("loading"); // loading | ok | blocked
  const [subscribing, setSubscribing] = useState(false);
  const [plan, setPlan] = useState({ price: 29, currency: "USD", plan_name: "EasyWaybill" });

  useEffect(() => {
    const token = localStorage.getItem("ff_access_token");
    if (!token) { setState("ok"); return; }
    axios.get(`${API}/shopify/billing/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const d = res.data || {};
        setPlan({ price: d.price ?? 29, currency: d.currency || "USD", plan_name: d.plan_name || "EasyWaybill" });
        setState(d.active ? "ok" : "blocked");
      })
      .catch(() => setState("ok")); // fail-open on transient errors — never lock out a paying merchant
  }, []);

  const startSubscribe = async () => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem("ff_access_token");
      const res = await axios.post(`${API}/shopify/billing/subscribe`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.confirmationUrl) {
        window.location.assign(res.data.confirmationUrl);
      } else {
        toast.error("Could not start checkout. Please try again.");
        setSubscribing(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setSubscribing(false);
    }
  };

  if (state === "loading") {
    return (
      <div data-testid="billing-gate-loading" className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Checking your subscription…
      </div>
    );
  }
  if (state === "blocked") {
    return (
      <div data-testid="billing-upgrade-screen" className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
          <div className="text-5xl mb-4">🚀</div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Subscribe to EasyWaybill</h1>
          <p className="text-sm text-slate-600 mb-6">
            Your free trial has ended. Subscribe to keep creating waybills, syncing orders and checking rates.
          </p>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-5 mb-6">
            <div className="text-3xl font-extrabold text-slate-900">
              ${plan.price}<span className="text-base font-medium text-slate-500">/month</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">{plan.currency} · billed monthly through Shopify</div>
          </div>
          <button
            data-testid="billing-subscribe-button"
            onClick={startSubscribe}
            disabled={subscribing}
            className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors disabled:opacity-60"
          >
            {subscribing ? "Opening Shopify…" : "Subscribe via Shopify"}
          </button>
          <p className="text-[11px] text-slate-400 mt-4">
            You'll approve the charge on Shopify's secure billing screen. Cancel anytime from your Shopify admin.
          </p>
        </div>
      </div>
    );
  }
  return children;
};

// Protected Route wrapper — redirects to /auth when shop param present and not logged in
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, login } = useAuth();
  
  if (!isAuthenticated) {
    // If shop param is in URL (coming from Shopify), redirect to auth page
    const params = new URLSearchParams(window.location.search);
    const shop = params.get('shop');
    if (shop) {
      return <Navigate to={`/auth?shop=${shop}`} replace />;
    }
    return <LoginPage onLogin={login} />;
  }
  
  return <OnboardingCheck><BillingGate>{children}</BillingGate></OnboardingCheck>;
};

// Onboarding check — shows wizard if no courier config exists
const OnboardingCheck = ({ children }) => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("ff_access_token");
    if (!token) { setChecked(true); return; }

    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/courier-config`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        // Skip wizard if already configured OR if defaults are available
        if (!res.data.configured && !res.data.account_number) {
          setShowOnboarding(true);
        }
      })
      .catch(() => {})
      .finally(() => setChecked(true));
  }, []);

  if (!checked) return null;

  return (
    <>
      {showOnboarding && (
        <OnboardingWizard onComplete={() => {
          setShowOnboarding(false);
          window.location.reload();
        }} />
      )}
      {children}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ShopifyAppBridgeProvider>
            <AppProvider>
              <Toaster 
                position="top-right" 
                richColors 
                toastOptions={{
                  style: { fontFamily: 'Inter, sans-serif' }
                }}
              />
              <Routes>
                {/* Default route - redirect to orders (Quick Collect) */}
                <Route
                  path="/"
                  element={<Navigate to="/orders" replace />}
                />

                {/* Quotes */}
                <Route path="/quotes/request" element={<ProtectedRoute><AppLayout><CreateWaybillPageWrapper mode="quote" /></AppLayout></ProtectedRoute>} />
                <Route path="/quotes/view" element={<ProtectedRoute><AppLayout><QuotesPage /></AppLayout></ProtectedRoute>} />
                <Route path="/quotes" element={<ProtectedRoute><AppLayout><QuotesPage /></AppLayout></ProtectedRoute>} />

                {/* Collections - Shopify Orders */}
                <Route path="/collections/new" element={<ProtectedRoute><AppLayout><CreateWaybillPageWrapper mode="return-new" /></AppLayout></ProtectedRoute>} />
                <Route path="/collections/elsewhere" element={<ProtectedRoute><AppLayout><CreateWaybillPageWrapper mode="return-elsewhere" /></AppLayout></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><AppLayout><OrdersPage /></AppLayout></ProtectedRoute>} />
                <Route path="/orders/:orderId" element={<ProtectedRoute><AppLayout><OrderDetailPage /></AppLayout></ProtectedRoute>} />
                <Route path="/orders/:orderId/waybill" element={<ProtectedRoute><AppLayout><CreateWaybillPageWrapper mode="order-waybill" /></AppLayout></ProtectedRoute>} />
                <Route path="/collections" element={<ProtectedRoute><AppLayout><WaybillsPage /></AppLayout></ProtectedRoute>} />

                {/* Waybills */}
                <Route path="/waybills/capture" element={<ProtectedRoute><AppLayout><CreateWaybillPageWrapper mode="capture" /></AppLayout></ProtectedRoute>} />
                <Route path="/waybills/summary" element={<ProtectedRoute><AppLayout><ManifestPage /></AppLayout></ProtectedRoute>} />
                {/* Cancel route removed - EasyWaybill API does not support cancellation */}
                <Route path="/waybills" element={<ProtectedRoute><AppLayout><WaybillsPage /></AppLayout></ProtectedRoute>} />
                
                {/* Manifest */}
                <Route path="/manifest" element={<ProtectedRoute><AppLayout><ManifestPage /></AppLayout></ProtectedRoute>} />

                {/* PODs route removed - API does not support POD retrieval */}

                {/* My Account */}
                <Route path="/addresses" element={<ProtectedRoute><AppLayout><AddressesPage /></AppLayout></ProtectedRoute>} />
                <Route path="/packages" element={<ProtectedRoute><AppLayout><PackagesPage /></AppLayout></ProtectedRoute>} />
                <Route path="/pricing" element={<ProtectedRoute><AppLayout><PricingPage /></AppLayout></ProtectedRoute>} />
                <Route path="/shipping-sync" element={<Navigate to="/pricing" replace />} />
                <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />

                {/* Shopify Order Waybill */}
                <Route path="/shopify/order/waybill" element={<ProtectedRoute><AppLayout><ShopifyOrderWaybillPage /></AppLayout></ProtectedRoute>} />

                {/* Auth page (login + register) */}
                <Route path="/auth" element={<AuthPage />} />

                {/* Public Pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/super-admin" element={<SuperAdminPage />} />

                <Route path="*" element={<Navigate to="/orders" replace />} />
              </Routes>
            </AppProvider>
          </ShopifyAppBridgeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Placeholder page for features not yet implemented
const PlaceholderPage = ({ title }) => (
  <div className="p-8">
    <h1 className="text-2xl font-semibold text-gray-800 mb-4">{title}</h1>
    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
      <div className="text-4xl mb-4">🚧</div>
      <p className="text-gray-600">This feature will be available when LIVE mode is enabled.</p>
      <p className="text-sm text-gray-400 mt-2">Data will come from EasyWaybill API</p>
    </div>
  </div>
);

export default App;
